import React, { Component } from 'react';
import { withApollo } from 'react-apollo';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { AppContext } from '../../AppProvider';

import Button from '@material-ui/core/Button';

import ErrorNotification from '../../components/common/ErrorNotification';
import LoadingIndicator from '../../components/LoadingIndicator';
import { Formik, Field } from 'formik';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import FormItemContainer from '../../components/Global/Containers/FormItemContainer';
import Grid from '@material-ui/core/Grid';
import GridItem from '../../components/Global/GridItem';
import TextFieldInput from '../../components/Global/Inputs/TextField';
import DateFieldInput from '../../components/Global/Inputs/DateField';
import PhoneField from '../../components/Global/Inputs/PhoneField';
import ProgressButton from '../../components/Global/Buttons/Progress';
import OutlinedSelectInput from '../../components/Global/Inputs/OutlinedSelect';
import _get from 'lodash/get';

import CarrierRelationshipModel, {
  sanitize
} from '../../Models/CarrierRelationship';
import CarrierRelationshipSchema from './CarrierRelationship.schema';
import * as Mutations from '../../graphQL/mutations/carrierRelationship';
import * as Queries from '../../graphQL/queries/carrierRelationship';

import CarrierRelationshipFormStyles from './__styles__/CarrierRelationshipForm.styles';

import { MINUTES_IN_A_HOUR, DEFAULT_NETTERMS } from '../../constants/constants';

export class CarrierRelationshipForm extends Component {
  state = {
    carrier: {},
    error: null,
    loading: true,
    referenceFields: PropTypes.object.isRequired
  };

  async componentDidMount() {
    const {
      match: {
        params: { id }
      }
    } = this.props;

    if (id) {
      await this._getData(id);
    } else {
      this.setState({
        carrier: { ...CarrierRelationshipModel() },
        loading: false
      });
    }
  }

  _getData = async id => {
    const { client: ApolloClient } = this.props;

    try {
      const {
        data: {
          carrier: { byId: carrier }
        }
      } = await ApolloClient.query({
        query: Queries.byId,
        variables: { id }
      });

      this.setState({
        carrier: {
          ...carrier,
          leadTimeWindow: carrier.leadTimeWindowMinutes / MINUTES_IN_A_HOUR,
          isNew: false
        }
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  };

  _handleOnSave = async values => {
    const { client: ApolloClient, history } = this.props;
    try {
      this.setState({ isSubmitting: true });
      const mutation = values.isNew ? Mutations.create : Mutations.update;
      const mutationType = values.isNew ? 'create' : 'update';

      const {
        data: {
          carrier: { [mutationType]: carrier }
        }
      } = await ApolloClient.mutate({
        mutation,
        variables: { carrier: sanitize(values) }
      });

      history.push(`/carrier-relationships/${carrier.id}`);
    } catch (error) {
      this.setState({
        errors: error.message,
        isSubmitting: false
      });
    }
  };

  render() {
    const { t, classes } = this.props;
    const { isPropelAdministrator } = this.context.globalProps;
    const { netTerms } = this.context.globalProps.referenceData;

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    const {
      orgInfo: {
        config: { defaultCountry }
      }
    } = this.context.globalProps;

    return (
      <Formik
        validationSchema={CarrierRelationshipSchema({ t })}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          ...this.state.carrier
        }}
        onSubmit={async values => {
          this._handleOnSave(values);
        }}
        render={({ values, handleChange, handleSubmit, dirty }) => (
          <div>
            <div className={classes.titleWrapper}>
              <h1
                className={classnames(
                  classes.headline4,
                  classes.noMarginBottom,
                  classes.title
                )}
              >
                {values.id ? values.name : t('Add Carrier Relationship')}
              </h1>
            </div>
            <FormItemContainer title={t('Basic Information')}>
              {this.state.loading ? (
                <LoadingIndicator />
              ) : (
                <Grid container spacing={16}>
                  <GridItem lg={3}>
                    <Field
                      name="name"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.name}
                            label={t('Carrier Name')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="externalId"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.externalId}
                            label={t('External ID')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="scac"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.scac}
                            label={t('SCAC')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="leadTimeWindow"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.leadTimeWindow}
                            label={t('Min. Lead Time')}
                            fullWidth
                            field={field}
                            form={form}
                            type={'number'}
                            endAdornment={'hrs'}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={9}>
                    <Field
                      name="ratingUrl"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.ratingUrl}
                            label={t('Rating URL')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      value={values.ratingPhoneNumber}
                      fullWidth
                      name={'ratingPhoneNumber'}
                      label={t('Rating Phone Number')}
                      onChange={handleChange}
                      component={PhoneField}
                      country={defaultCountry}
                    />
                  </GridItem>
                  <GridItem lg={9}>
                    <Field
                      name="dispatchUrl"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.dispatchUrl}
                            label={t('Dispatch URL')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      value={values.dispatchPhoneNumber}
                      fullWidth
                      name={'dispatchPhoneNumber'}
                      label={t('Dispatch Phone Number')}
                      onChange={handleChange}
                      component={PhoneField}
                      country={defaultCountry}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      value={values.customerServicePhoneNumber}
                      fullWidth
                      name={'customerServicePhoneNumber'}
                      label={t('Customer Service Phone Number')}
                      onChange={handleChange}
                      component={PhoneField}
                      country={defaultCountry}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="mcNumber"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.mcNumber}
                            label={t('MC Number')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="dunsNumber"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.dunsNumber}
                            label={t('DUNS Number')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="usDotNumber"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.usDotNumber}
                            label={t('US DOT Number')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="federalEinNumber"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.federalEinNumber}
                            label={t('Federal EIN Number')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="netTermsId"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <OutlinedSelectInput
                            value={_get(values, 'netTermsId', DEFAULT_NETTERMS)}
                            field={field}
                            form={form}
                            label={t('Net Terms')}
                            options={netTerms}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  {isPropelAdministrator && (
                    <GridItem lg={3}>
                      <Field
                        name="p44AccountCode"
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <TextFieldInput
                              value={values.p44AccountCode}
                              label={t('P44 Account Code')}
                              fullWidth
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                  )}
                </Grid>
              )}
            </FormItemContainer>

            <FormItemContainer title={t('Insurance Information')}>
              {this.state.loading ? (
                <LoadingIndicator />
              ) : (
                <Grid container spacing={16}>
                  <GridItem lg={3}>
                    <Field
                      name="insuranceProvider"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.insuranceProvider}
                            label={t('Insurance Provider')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name="insurancePolicyNumber"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.insurancePolicyNumber}
                            label={t('Policy Number')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name={'insuranceEffectiveDate'}
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <DateFieldInput
                          value={values.insuranceEffectiveDate}
                          field={field}
                          form={form}
                          label={t('Policy Starts')}
                          format={'MMM DD, YYYY'}
                          disablePast={false}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name={'insuranceExpirationDate'}
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <DateFieldInput
                          value={values.insuranceExpirationDate}
                          field={field}
                          form={form}
                          label={t('Policy Ends')}
                          format={'MMM DD, YYYY'}
                        />
                      )}
                    />
                  </GridItem>
                </Grid>
              )}
            </FormItemContainer>

            <div className={classes.actionsWrapper}>
              <Button
                color={'primary'}
                className={classnames(classes.button, classes.actionButtons)}
                classes={{
                  label: classes.buttonLabel
                }}
                component={Link}
                to={
                  values.isNew
                    ? '/carrier-relationships'
                    : `/carrier-relationships/${values.id}`
                }
              >
                {t('Cancel')}
              </Button>
              <ProgressButton
                color={'primary'}
                className={classnames(classes.button, classes.actionButtons)}
                classes={{
                  label: classes.buttonLabel,
                  disabled: classnames(
                    classes.buttonDisabled,
                    classes.actionButtonDisabled
                  )
                }}
                disabled={!dirty}
                onClick={handleSubmit}
                isSubmitting={this.state.isSubmitting}
                variant={!dirty ? 'contained' : 'text'}
              >
                {values.isNew ? t('Add Relationship') : t('Save Relationship')}
              </ProgressButton>
            </div>
            {this.state.errors && (
              <ErrorNotification message={this.state.errors} />
            )}
          </div>
        )}
      />
    );
  }
}

CarrierRelationshipForm.contextType = AppContext;

const CarrierRelationshipFormWithTranslation = withNamespaces()(
  CarrierRelationshipForm
);

export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    CarrierRelationshipFormStyles
  )
)(withApollo(CarrierRelationshipFormWithTranslation));
